import React, { useEffect, useState } from 'react';
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Product } from '../../../types';
import { ProductSelectionForm } from '../ProductSelectionForm';

interface ProductEditCellProps extends GridRenderEditCellParams {
  api: GridRenderEditCellParams['api'];
  products: Product[];
}

const ProductEditCell: React.FC<ProductEditCellProps> = (props) => {
  const { api, id, value, products } = props;

  // Helper function to find product by name or id
  const findProduct = (searchValue: string): Product | null => {
    if (!searchValue) return null;

    // First try to find by exact name match
    let product = products.find((p) => p.name === searchValue);
    if (product) return product;

    // Then try to find by ID
    product = products.find((p) => p.id === searchValue);
    if (product) return product;

    // Finally try fuzzy match on name (case insensitive, partial match)
    product = products.find(
      (p) =>
        p.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        searchValue.toLowerCase().includes(p.name.toLowerCase())
    );

    return product || null;
  };

  // Find the product that matches the current productName value
  const initialProduct = findProduct(value as string);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(initialProduct);
  const [productSearchQuery, setProductSearchQuery] = useState<string>(
    initialProduct ? `${initialProduct.family} - ${initialProduct.name}` : (value as string) || ''
  );

  // Update the selected product when the value prop changes
  useEffect(() => {
    const newProduct = findProduct(value as string);
    if (newProduct !== selectedProduct) {
      setSelectedProduct(newProduct);
      setProductSearchQuery(newProduct ? `${newProduct.family} - ${newProduct.name}` : (value as string) || '');
    }
  }, [value, products, selectedProduct]);

  const handleInputChange = (e: React.SyntheticEvent<Element, Event>, inputValue: string | null) => {
    setProductSearchQuery(inputValue || '');
  };

  const handleProductSelectionChange = (e: React.SyntheticEvent<Element, Event>, product: Product | null) => {
    setSelectedProduct(product);

    if (product) {
      // Update productName in the current edit cell
      api.setEditCellValue({ id, field: 'productName', value: product.name }, undefined);

      // Update the entire row to ensure productId and product object are set
      const currentRow = api.getRow(id);
      api.updateRows([
        {
          ...currentRow,
          id,
          productName: product.name,
          productId: product.id,
          product: product
        }
      ]);

      setProductSearchQuery(`${product.family} - ${product.name}`);
    } else {
      // Clear product fields but keep the search query for partial input
      api.setEditCellValue({ id, field: 'productName', value: productSearchQuery }, undefined);

      // Update the entire row to clear productId and product object
      const currentRow = api.getRow(id);
      api.updateRows([
        {
          ...currentRow,
          id,
          productName: productSearchQuery,
          productId: '',
          product: null
        }
      ]);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center'
      }}>
      <div style={{ width: '100%' }}>
        <ProductSelectionForm
          product={selectedProduct}
          allProducts={products}
          productSearchQuery={productSearchQuery}
          handleInputChange={handleInputChange}
          handleProductSelectionChange={handleProductSelectionChange}
          size="small"
          classNames="w-full"
          // Pass style overrides for the underlying TextField/Input
          textFieldProps={{
            InputProps: {
              sx: {
                height: '100%',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': {
                  padding: '8px',
                  height: 'auto'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                }
              }
            },
            InputLabelProps: {
              sx: {
                display: 'none'
              }
            }
          }}
        />
      </div>
      {/* Style overrides moved to props below */}
    </div>
  );
};

export default ProductEditCell;
